import { Capacitor } from "@capacitor/core";

/**
 * SULL'APP PER IPHONE: ISCRIZIONE SÌ, INVITI A COMPRARE NO (Apple 3.1.1).
 *
 * Il 29/09/2026 Apple ha respinto la build 8: «The app includes an account
 * registration feature for businesses and organizations, which is considered
 * access to external mechanisms for purchases». Il punto non era l'iscrizione:
 * era il muro dopo i 7 giorni, con «richiedi l'attivazione a Datacom» e i tasti
 * che portavano alla richiesta. Per Apple quello è un invito a comprare fuori.
 *
 * Decisione di Davide (29/09): l'iscrizione resta nell'app anche su iPhone, e
 * crea un account GRATUITO che non scade (gli strumenti restano sempre liberi,
 * come le app «gratis + abbonamento sul sito»: Spotify, Slack). Dentro l'app
 * iOS però nessun prezzo, nessun tasto o testo che porti a richiedere o
 * comprare l'account installatore: la licenza si propone FUORI dall'app (mail
 * di benvenuto e del 5° giorno, Datacom al telefono), che Apple consente.
 *
 * Il sito e l'app Android restano come sono.
 */
export function invitiAllAcquistoNellApp(): boolean {
  return Capacitor.getPlatform() !== "ios";
}

/** Testi neutri per l'iPhone, al posto di quelli che mandano a Datacom per l'attivazione. */
export const TESTI_IOS = {
  valutazioneFinita:
    "Il periodo di valutazione dell'assistente è terminato. Gli strumenti di calcolo restano a tua disposizione.",
  accessoNonAttivo:
    "L'assistente non è attivo per il tuo account in questo momento. Gli strumenti di calcolo restano a tua disposizione.",
  quotaSettimana: "Hai raggiunto le domande di questa settimana: il conteggio riparte lunedì.",
  quotaValutazione: "Hai usato le domande del periodo di valutazione. Gli strumenti di calcolo restano a tua disposizione.",
} as const;

/**
 * L'ACCOUNT GRATUITO DELL'IPHONE (03/10/2026, dopo il terzo rifiuto di Apple:
 * lo screenshot del revisore era proprio il modulo d'iscrizione con Azienda e
 * Partita IVA e i «primi 7 giorni»). Decisione di Davide: l'iscrizione resta,
 * ma sull'iPhone crea un account gratuito che non scade - strumenti di calcolo
 * e 3 domande a settimana sulle norme - senza dati d'impresa e senza prova a
 * tempo. La licenza non si nomina mai nell'app; chi ce l'ha entra e la usa.
 * Il server riconosce l'iPhone dall'intestazione x-piattaforma (chatClient).
 */
export function datiImpresaNellIscrizione(): boolean {
  return Capacitor.getPlatform() !== "ios";
}

export const DOMANDE_GRATUITE_SETTIMANA = 3;

/** Il riquadro sopra «Crea account» sull'iPhone: cosa si riceve, e basta. */
export const TESTO_ISCRIZIONE_IOS = (): string =>
  `Con la registrazione crei un account che non scade: gli strumenti di calcolo e ${DOMANDE_GRATUITE_SETTIMANA} domande a settimana all'assistente su norme e conformità.`;

/** La tessera dell'account sull'iPhone, per chi non ha la licenza. */
export const TESSERA_GRATUITA_IOS = {
  titolo: "Account gratuito",
  dettaglio: `Strumenti di calcolo e ${DOMANDE_GRATUITE_SETTIMANA} domande a settimana sulle norme`,
} as const;

/**
 * La risposta del motore sui marchi fuori dall'accesso arriva gia' scritta dal
 * server (Dify, nodo codice), che non sa da che piattaforma parte la domanda:
 * «X non è incluso nel tuo abbonamento… Per aggiungere un marchio, scrivi a
 * Datacom.» Sull'iPhone si toglie l'invito e la parola «abbonamento» (01/10).
 */
export function rispostaPerQuestaApp(testo: string): string {
  if (invitiAllAcquistoNellApp()) return testo;
  return testo
    .replace(/\s*Per aggiungere (?:un|altri) marc\w*,? scrivi a Datacom\.?/gi, "")
    .replace(/nel tuo abbonamento/gi, "nel tuo accesso");
}

/**
 * La dichiarazione di conformita' e' della licenza (Davide, 03/10): sull'iPhone
 * l'account gratuito non la vede. Ce l'ha chi ha marchi validi, chi e' ancora
 * nei 7 giorni con la partita IVA (account nato sul sito) e l'admin. Fuori
 * dall'iPhone decide la pagina come prima (blocco a valutazione finita).
 */
type ProfiloLicenza = { brands?: string[] | null; scadenza?: string | null; created_at?: string | null; partita_iva?: string | null };

/** Ha marchi e la scadenza non e' passata: e' un account con licenza. */
export function licenzaValida(profile: ProfiloLicenza | null): boolean {
  if (!profile) return false;
  const scad = profile.scadenza ? Date.parse(profile.scadenza) : null;
  return (profile.brands ?? []).length > 0 && (scad === null || Number.isNaN(scad) || scad > Date.now());
}

export function dicoNellApp(profile: ProfiloLicenza | null, isAdmin = false): boolean {
  if (Capacitor.getPlatform() !== "ios") return true;
  if (isAdmin) return true;
  if (!profile) return false;
  if (licenzaValida(profile)) return true;
  const nato = Date.parse(profile.created_at ?? "");
  const inProva = !Number.isNaN(nato) && Date.now() - nato < 7 * 86_400_000;
  return inProva && Boolean(profile.partita_iva);
}

/**
 * LA CHAT NON SI BLOCCA DALL'APP SULL'IPHONE (04/10/2026, audit). Il client
 * calcola «scaduto» per chi non ha licenza da piu' di 7 giorni, e fino alla
 * 126 su iPhone spegneva il campo e apriva «periodo di valutazione terminato»
 * - mentre il server da' a quell'account le 3 domande gratuite. Sull'iPhone il
 * limite lo mette solo il server (429 «domande di questa settimana»).
 */
export function chatBloccataQui(stato: { tipo: string } | null | undefined): boolean {
  return stato?.tipo === "scaduto" && invitiAllAcquistoNellApp();
}

/**
 * Esempi per chi sull'iPhone ha l'account gratuito: solo norme e prassi, che
 * e' cio' a cui il gratuito risponde. Proporre domande sui marchi gli faceva
 * spendere le 3 domande per sentirsi dire «non incluso».
 */
export const ESEMPI_NORME = [
  "Quali norme cito nella dichiarazione di conformità per un impianto antintrusione?",
  "Qual è l'edizione in vigore della UNI 9795?",
  "Come dimensiono la batteria tampone per 24 ore di autonomia?",
] as const;

export function soloEsempiNorme(profile: ProfiloLicenza | null): boolean {
  return Capacitor.getPlatform() === "ios" && !licenzaValida(profile);
}

/**
 * CONSENSO PER L'INTELLIGENZA ARTIFICIALE DI TERZI (Apple 5.1.2(i), 04/10/2026).
 * Domande, foto e voce vanno a OpenAI: sull'iPhone si chiede un permesso
 * esplicito una volta, prima del primo uso dell'assistente. Chi si iscrive con
 * Google o Apple non spunta nessuna casella, quindi la casella del modulo non
 * basta. Il permesso resta sul telefono, per account.
 */
export function consensoAiRichiesto(): boolean {
  return Capacitor.getPlatform() === "ios";
}

const CHIAVE_CONSENSO_AI = "datacom.consenso_ai.";

export function consensoAiDato(userId: string | null | undefined): boolean {
  if (!consensoAiRichiesto()) return true;
  if (!userId) return false;
  try {
    return window.localStorage.getItem(CHIAVE_CONSENSO_AI + userId) === "si";
  } catch {
    return false;
  }
}

export function registraConsensoAi(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(CHIAVE_CONSENSO_AI + userId, "si");
  } catch {
    /* memoria non disponibile: il permesso verra' richiesto di nuovo */
  }
}
